import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Grid,
  Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Alert, Card, CardContent
} from '@mui/material';
import { School, EmojiEvents, CheckCircle } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const api = {
  get:  (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
  post: (url, data)   => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
};

const CLASS_COLORS = {
  summa_cum_laude: '#7b1fa2', cum_laude: '#1565c0', distinction: '#c9952a',
  merit: '#2e7d32', pass: '#546e7a'
};

const CLASS_LABELS = {
  summa_cum_laude: 'Summa Cum Laude', cum_laude: 'Cum Laude',
  distinction: 'Distinction', merit: 'Merit', pass: 'Pass'
};

export default function GraduationManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [graduations, setGraduations]   = useState([]);
  const [eligible, setEligible]         = useState([]);
  const [programmes, setProgrammes]     = useState([]);
  const [tab, setTab]                   = useState('eligible'); // eligible | graduated
  const [dialog, setDialog]             = useState(false);
  const [selected, setSelected]         = useState(null);
  const [saving, setSaving]             = useState(false);
  const [form, setForm] = useState({ graduation_date:'', ceremony_date:'' });

  const load = async () => {
    try {
      // Load graduated
      const g = await api.get('/tertiary/graduations');
      setGraduations(g.data.data || []);

      // Load eligible (enrolled students with enough credits)
      const e = await api.get('/tertiary/enrolments', { params: { status: 'active' } });
      setProgrammes((await api.get('/tertiary/programmes')).data.data || []);
      // Filter students with GPA >= 2.0 as eligible
      setEligible((e.data.data || []).filter(s => parseFloat(s.gpa) >= 2.0 && s.total_credits_earned > 0));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const openGraduate = (student) => {
    setSelected(student);
    setForm({ graduation_date: new Date().toISOString().split('T')[0], ceremony_date: '' });
    setDialog(true);
  };

  const graduate = async () => {
    if (!form.graduation_date) return enqueueSnackbar('Graduation date required', { variant: 'warning' });
    setSaving(true);
    try {
      const r = await api.post('/tertiary/graduations', {
        student_id: selected.student_id,
        programme_id: selected.programme_id,
        graduation_date: form.graduation_date,
        ceremony_date: form.ceremony_date || null,
        final_gpa: selected.gpa,
      });
      enqueueSnackbar(`Graduated! Certificate: ${r.data.certificate_number}`, { variant: 'success' });
      setDialog(false); load();
    } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const gpaColor = (gpa) => {
    const g = parseFloat(gpa);
    if (g >= 3.7) return '#7b1fa2';
    if (g >= 3.3) return '#1565c0';
    if (g >= 3.0) return '#2e7d32';
    if (g >= 2.0) return '#c9952a';
    return '#c62828';
  };

  const getClassification = (gpa) => {
    const g = parseFloat(gpa);
    if (g >= 3.7) return 'Summa Cum Laude';
    if (g >= 3.3) return 'Cum Laude';
    if (g >= 2.5) return 'Distinction';
    if (g >= 2.0) return 'Merit';
    return 'Pass';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Graduation Management</Typography>
          <Typography variant="body2" color="text.secondary">Graduate students and issue certificate numbers</Typography>
        </Box>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label:'Eligible to Graduate', value: eligible.length, icon:<School/>, color:'#2e7d32' },
          { label:'Graduated', value: graduations.length, icon:<EmojiEvents/>, color:'#c9952a' },
          { label:'Programmes', value: programmes.length, icon:<CheckCircle/>, color:'#0f2b4a' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Card variant="outlined" sx={{ borderLeft:`4px solid ${s.color}`, borderRadius:2 }}>
              <CardContent sx={{ pb:'12px !important' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontSize={10} textTransform="uppercase" fontWeight={700}>{s.label}</Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                  </Box>
                  <Box sx={{ color: s.color, opacity: 0.3 }}>{s.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant={tab==='eligible'?'contained':'outlined'} onClick={()=>setTab('eligible')} sx={tab==='eligible'?{bgcolor:'#0f2b4a'}:{}}>
          Eligible Students ({eligible.length})
        </Button>
        <Button variant={tab==='graduated'?'contained':'outlined'} onClick={()=>setTab('graduated')} sx={tab==='graduated'?{bgcolor:'#c9952a'}:{}}>
          Graduated ({graduations.length})
        </Button>
      </Stack>

      {/* Eligible Students */}
      {tab === 'eligible' && (
        <>
          {eligible.length === 0 && (
            <Alert severity="info">No students eligible for graduation yet. Students need a GPA ≥ 2.0 and completed credits.</Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor:'#f8f9fc' }}>
                {['Student','Student No.','Programme','Credits','GPA','Classification','Action'].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {eligible.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell><Typography fontWeight={600} fontSize={13}>{s.last_name} {s.first_name}</Typography></TableCell>
                    <TableCell><Typography fontFamily="monospace" fontSize={12}>{s.student_number}</Typography></TableCell>
                    <TableCell><Typography fontSize={12} color="text.secondary">{s.programme_name}</Typography></TableCell>
                    <TableCell><Chip label={s.total_credits_earned} size="small" color="info"/></TableCell>
                    <TableCell>
                      <Typography fontWeight={800} sx={{ color: gpaColor(s.gpa) }}>{parseFloat(s.gpa).toFixed(3)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getClassification(s.gpa)} size="small"
                        sx={{ bgcolor: gpaColor(s.gpa), color:'white', fontWeight:700 }}/>
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" onClick={()=>openGraduate(s)}
                        sx={{ bgcolor:'#2e7d32', fontSize:11 }} startIcon={<EmojiEvents sx={{ fontSize:14 }}/>}>
                        Graduate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Graduated */}
      {tab === 'graduated' && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor:'#f8f9fc' }}>
              {['Student','Student No.','Programme','GPA','Classification','Certificate No.','Date'].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {graduations.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py:4, color:'text.secondary' }}>No graduates yet</TableCell></TableRow>
              ) : graduations.map(g => (
                <TableRow key={g.id} hover>
                  <TableCell><Typography fontWeight={600} fontSize={13}>{g.last_name} {g.first_name}</Typography></TableCell>
                  <TableCell><Typography fontFamily="monospace" fontSize={12}>{g.student_number}</Typography></TableCell>
                  <TableCell><Typography fontSize={12} color="text.secondary">{g.programme_name}</Typography></TableCell>
                  <TableCell><Typography fontWeight={800} sx={{ color: gpaColor(g.final_gpa) }}>{parseFloat(g.final_gpa||0).toFixed(3)}</Typography></TableCell>
                  <TableCell>
                    <Chip label={CLASS_LABELS[g.classification]||g.classification} size="small"
                      sx={{ bgcolor: CLASS_COLORS[g.classification]||'#546e7a', color:'white', fontWeight:700 }}/>
                  </TableCell>
                  <TableCell><Typography fontFamily="monospace" fontSize={12} fontWeight={700}>{g.certificate_number}</Typography></TableCell>
                  <TableCell><Typography fontSize={12}>{g.graduation_date ? new Date(g.graduation_date).toLocaleDateString() : '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Graduate Dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#2e7d32', color:'white' }}>
          Graduate — {selected?.first_name} {selected?.last_name}
        </DialogTitle>
        <DialogContent sx={{ pt:'20px !important' }}>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="success" icon={<EmojiEvents/>}>
                  <strong>{selected.programme_name}</strong><br/>
                  GPA: <strong>{parseFloat(selected.gpa).toFixed(3)}</strong> &nbsp;·&nbsp;
                  Classification: <strong>{getClassification(selected.gpa)}</strong> &nbsp;·&nbsp;
                  Credits: <strong>{selected.total_credits_earned}</strong>
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Graduation Date *" type="date"
                  value={form.graduation_date} onChange={e=>setForm(f=>({...f,graduation_date:e.target.value}))}
                  InputLabelProps={{ shrink:true }}/>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Ceremony Date" type="date"
                  value={form.ceremony_date} onChange={e=>setForm(f=>({...f,ceremony_date:e.target.value}))}
                  InputLabelProps={{ shrink:true }} helperText="Optional"/>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize:12 }}>
                  A unique certificate number will be auto-generated upon graduation.
                  The student's enrolment status will be updated to <strong>Graduated</strong>.
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={graduate} disabled={saving} sx={{ bgcolor:'#2e7d32' }}>
            {saving ? 'Processing...' : 'Confirm Graduation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
